import { OddsButton } from '@/components/widgets/odds-button';
import { useTranslations } from 'next-intl';
import { useAccountInfo } from '@/hooks/query/account';
import { BetTypeContentProps } from '../bet-type-content';
import { useState, useEffect } from 'react';
import { DropdownIcon } from '@/components/icons/svg-icons';
import { BetChoice } from '@/features/odds-page/types';
import { Dropdown, DropdownEvent } from '@nf/ui';
import { useGetBetTypeSetting } from '@/hooks';

export const BetTypeHalfTimeFullTimeLinked = ({ matchModel, betTypeArray }: BetTypeContentProps) => {
	const getBetTypeSetting = useGetBetTypeSetting();
	const market = betTypeArray[0];

	const betTypeSetting = getBetTypeSetting(market.BetTypeId, matchModel.GameID);

	const { betChoices } = betTypeSetting;

	const t = useTranslations('General');
	const { user } = useAccountInfo();

	const defaultHalfTimeScore = t('lbl_1st_half');
	const defaultFullTimeScore = t('lbl_fulltime');

	const [isOpen, setIsOpen] = useState(false);
	const [halfTimeScores, setHalfTimeScores] = useState<Array<string>>([]);
	const [fullTimeScores, setFullTimeScores] = useState<Array<string>>([]);

	const [currentHalfTimeScore, setCurrentHalfTimeScore] = useState<string>(defaultHalfTimeScore);
	const [currentFullTimeScore, setCurrentFullTimeScore] = useState<string>(defaultFullTimeScore);

	useEffect(() => {
		const halfTimeScoreSet = new Set<string>();

		betChoices.forEach(choice => {
			const halfTimeScore = choice.label.split('/')[0];
			halfTimeScoreSet.add(halfTimeScore);
		});

		setHalfTimeScores([defaultHalfTimeScore, ...halfTimeScoreSet]);
		setCurrentHalfTimeScore(defaultHalfTimeScore);
	}, []);
	
	useEffect(() => {
		const fullTimeScoreSet = new Set<string>();

		betChoices.filter(choice => {
			return choice.label.startsWith(currentHalfTimeScore);
		}).forEach(choice => {
			const splitLabel = choice.label.split('/');
			const fullTimeScore = splitLabel[1] ?? splitLabel[0];
			fullTimeScoreSet.add(fullTimeScore);
		});

		setFullTimeScores([defaultFullTimeScore, ...fullTimeScoreSet]);
		
		if (!fullTimeScoreSet.has(currentFullTimeScore)) {
			setCurrentFullTimeScore(defaultFullTimeScore);
		}
	}, [currentHalfTimeScore]);

	const toggleIsOpen = () => {
		setIsOpen(!isOpen);
	}

	const handleHalfTimeScoreChange = (e: DropdownEvent) => {
		setCurrentHalfTimeScore(e.option.value);
	};

	const handleFullTimeScoreChange = (e: DropdownEvent) => {
		setCurrentFullTimeScore(e.option.value);
	};

	const betChoiceGroups = betChoices.reduce((result, betChoice) => {
		const halfScore = betChoice.label.split('/')[0];

		// 用上半場比分分群
		const existingGroup = result.find(group => group.some(choice => choice.label.startsWith(halfScore)));

		if (existingGroup) {
			existingGroup.push(betChoice);
		} else {
			result.push([betChoice]);
		}

		return result;
	}, new Array<Array<BetChoice>>());

	const targetChoice = currentFullTimeScore != defaultFullTimeScore && currentHalfTimeScore != defaultHalfTimeScore ?
		betChoices.find(betChoice => {
			return betChoice.label.startsWith(currentHalfTimeScore) &&
				betChoice.label.endsWith(currentFullTimeScore);
		}) : null;

	return (
		<div className="bettype__content bettype__content--htft">
			<div className="bettype__filter">
				<Dropdown
					placeholder={defaultHalfTimeScore}
					value={currentHalfTimeScore}
					options={halfTimeScores.map(score => {
						return { label: score, value: score }
					})}
					onChange={handleHalfTimeScoreChange}
					iconTemplate={() => <DropdownIcon />}
				/>
				<Dropdown
					placeholder={defaultFullTimeScore}
					value={currentFullTimeScore}
					options={fullTimeScores.map(score => {
						return { label: score, value: score }
					})}
					onChange={handleFullTimeScoreChange}
					iconTemplate={() => <DropdownIcon />}
				/>
				{
					targetChoice ?
						<OddsButton
							key={targetChoice.label}
							match={matchModel}
							market={market}
							selection={market.Selections[targetChoice.betTeam]}
							homeTeamName={''}
							awayTeamName={''}
							variant="center"
							betTeam={market.Selections[targetChoice.betTeam].SelId}
							originalPrice={market.Selections[targetChoice.betTeam].Price}
							sport={matchModel?.GameID}
							type="OU"
							oddsType={user?.OType}
							accountStatus={user?.ActStatus ?? 0}
						/> : 
						<div className="odds-button" data-odds-status="close" data-selected="false">
							<span className="odds-button__close">--</span>
						</div>
				}
				<div 
					className="bettype__more" 
					data-open={isOpen}
					onClick={toggleIsOpen}
				>
					<div className="bettype__more-text">{t(isOpen ? 'lbl_HideAll' : 'lbl_ViewAll')}</div>
					<DropdownIcon />
				</div>
			</div>
			{
				isOpen ? 
					<div className="bettype__filter-content" data-open={isOpen}>
						{
							betChoiceGroups.map(group => {
								return (
									<div key={group[0].label} className="bettype__filter-group">
										{
											group.map((choice, index) => {
												const key = `${market.BetTypeId}_${choice.betTeam || index}`;
	
												return (
													<div key={key} className="bettype__item">
														<div className="bettype__title">{choice.label}</div>
														<OddsButton
															key={`${key}`}
															match={matchModel}
															market={market}
															selection={market.Selections[choice.betTeam]}
															homeTeamName={''}
															awayTeamName={''}
															variant="center"
															betTeam={market.Selections[choice.betTeam].SelId}
															originalPrice={market.Selections[choice.betTeam].Price}
															sport={matchModel?.GameID}
															type="OU"
															oddsType={user?.OType}
															accountStatus={user?.ActStatus ?? 0}
														/>
													</div>
												);
											})
										}
									</div>
								);
							})
						}
					</div> : null
			}
		</div>
	)
};
